/*
  Перелёты ядра (R41): кадры WAAPI, общие для главного меню и перехода после входа. Ядро — один
  объект на всём пути, поэтому форма капли, вытяжка и темп живут в одном месте.

  Меню: капля вытекает из знака (чуть набухает), вытягивается в полёте, садится на значок пункта
  и растекается в выделение; обратно — выделение стягивается в каплю на значке и летит домой. Знак
  и значки пунктов стоят в одном столбце, поэтому полёт вертикальный.

  Вход (ShellEntrance): ядро по дуге летит в дом в меню — разгоняется, чуть вытягивается по ходу,
  замедляется и уменьшается до размера ядра в знаке меню.
*/

export const FLIGHT_OUT_MS = 740;
export const FLIGHT_IN_MS = 680;
/** Половина значка пункта: центр значка — место, где капля садится и откуда растекается. */
export const ICON_HALF = 24;
const PILL_RADIUS = 'var(--radius-md)';
export const ACCELERATE = 'cubic-bezier(0.5, 0, 0.9, 0.5)';
export const DECELERATE = 'cubic-bezier(0.1, 0.5, 0.3, 1)';

/** Ядро-капля: центр и радиус, в пикселях. */
export interface Drop {
  x: number;
  y: number;
  r: number;
}

/** Место выделения на пункте, в пикселях от меню. */
export interface PillBox {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Капля с центром в точке: круг в k раз больше ядра или вытянутая (доли ширины и высоты). */
export function drop(c: Drop, k = 1, stretch?: { w: number; h: number }): Keyframe {
  const w = 2 * c.r * (stretch ? stretch.w : k);
  const h = 2 * c.r * (stretch ? stretch.h : k);
  return {
    top: `${c.y - h / 2}px`,
    left: `${c.x - w / 2}px`,
    width: `${w}px`,
    height: `${h}px`,
    borderRadius: '50%',
    opacity: 1,
  };
}

export function pillFrame(b: PillBox): Keyframe {
  return {
    top: `${b.top}px`,
    left: `${b.left}px`,
    width: `${b.width}px`,
    height: `${b.height}px`,
    borderRadius: PILL_RADIUS,
    opacity: 1,
  };
}

/** Где выделение сейчас, с учётом идущего полёта. */
export function frameOf(el: HTMLElement): Keyframe {
  const cs = getComputedStyle(el);
  return {
    top: cs.top,
    left: cs.left,
    width: cs.width,
    height: cs.height,
    borderRadius: cs.borderRadius,
    opacity: 1,
  };
}

/** Ядро → выделение на пункте b. fresh — из знака; иначе капля уже в пути и летит дальше с места. */
export function outFrames(from: Keyframe, core: Drop, b: PillBox, fresh: boolean): Keyframe[] {
  const icon = { x: b.left + ICON_HALF, y: b.top + b.height / 2, r: core.r };
  const mid = { x: (core.x + icon.x) / 2, y: (core.y + icon.y) / 2, r: core.r };
  const land: Keyframe[] = [
    { ...drop(icon, 1.4), offset: 0.66, easing: 'cubic-bezier(0.3, 1.2, 0.5, 1)' },
    pillFrame(b),
  ];
  return fresh
    ? [
        { ...from, easing: 'cubic-bezier(0.3, 0, 0.6, 1)' },
        { ...drop(core, 1.35), offset: 0.14, easing: ACCELERATE },
        { ...drop(mid, 1, { w: 0.8, h: 2.6 }), offset: 0.45, easing: DECELERATE },
        ...land,
      ]
    : [{ ...from, easing: DECELERATE }, ...land];
}

/** Выделение на пункте b → ядро. fresh — с места выделения; иначе капля уже в пути. */
export function inFrames(from: Keyframe, core: Drop, b: PillBox, fresh: boolean): Keyframe[] {
  const icon = { x: b.left + ICON_HALF, y: b.top + b.height / 2, r: core.r };
  const mid = { x: (core.x + icon.x) / 2, y: (core.y + icon.y) / 2, r: core.r };
  const home: Keyframe[] = [
    { ...drop(mid, 1, { w: 0.8, h: 2.6 }), offset: 0.62, easing: DECELERATE },
    { ...drop(core, 1.3), offset: 0.86, easing: 'ease-out' },
    drop(core),
  ];
  return fresh
    ? [
        { ...from, easing: 'cubic-bezier(0.5, 0, 0.3, 1)' },
        { ...drop(icon, 1.4), offset: 0.3, easing: ACCELERATE },
        ...home,
      ]
    : [{ ...from, easing: ACCELERATE }, ...home];
}

/** Кривая дуги входа: разгон и мягкое торможение у дома. */
export const ARC_EASING = 'cubic-bezier(0.45, 0, 0.2, 1)';

/** Вытяжка вдоль направления полёта deg: sx — вдоль, sy — поперёк, s — общий масштаб. */
export function stretchAlong(deg: number, sx: number, sy: number, s: number): string {
  return `rotate(${deg}deg) scale(${sx * s}, ${sy * s}) rotate(${-deg}deg)`;
}

/**
 * Дуга входа (ShellEntrance): ядро из точки на сцене смещается на dx, dy и уменьшается до scale.
 * Середина пути смещена поперёк направления — полёт, а не перенос по прямой.
 */
export function entranceArc(dx: number, dy: number, scale: number): Keyframe[] {
  const angle = Math.atan2(dy, dx);
  const bend = Math.hypot(dx, dy) * 0.18;
  const mx = dx * 0.5 + Math.sin(angle) * bend;
  const my = dy * 0.5 - Math.cos(angle) * bend;
  const deg = (angle * 180) / Math.PI;
  const mid = Math.max(scale, 0.45);
  return [
    { transform: `translate(0px, 0px) ${stretchAlong(deg, 1, 1, 1)}` },
    {
      transform: `translate(${mx * 0.35}px, ${my * 0.35}px) ${stretchAlong(deg, 1.12, 0.9, 0.9)}`,
      offset: 0.25,
    },
    { transform: `translate(${mx}px, ${my}px) ${stretchAlong(deg, 1.22, 0.84, mid)}`, offset: 0.55 },
    { transform: `translate(${dx}px, ${dy}px) ${stretchAlong(deg, 1, 1, scale)}` },
  ];
}

/*
  Передача ядра помощнику (spec-3-motion §7): ядро уходит из дома каплей того же размера и возвращается.
  Кадры — куски перелёта R41, сами перелёты не меняются. Знак: подготовка — ядро набухает (×1,35) и
  сдвигается на 1 px против выхода, обратно — садится с ×1,3. Выделение: таблетка стягивается в каплю
  ×1,4 у левой кромки, левее значка (как первый отрезок inFrames), обратно — растекается из неё (как хвост
  outFrames). Не на самом значке: таблетка лежит под его линиями, а капля курьера — поверх меню; передача на
  значке дала бы кадр, где линии значка вдруг уходят под каплю (шов глубины).
*/

/** Ядро набухает перед уходом из знака. */
export const SWELL = 1.35;
/** Капля на значке пункта: больше ядра — так она читается частью таблетки. */
export const LAND_K = 1.4;
/** Ядро, вернувшееся в знак, садится с этого размера. */
export const SETTLE_K = 1.3;
/** Подготовка в знаке (набухание), мс. */
export const SWELL_MS = 100;
/** Таблетка стягивается в каплю, мс. */
export const CONTRACT_MS = 220;
/** Ядро садится в знак, мс. */
export const SETTLE_MS = 240;
/** Капля растекается в таблетку, мс. */
export const SPREAD_MS = 260;
/** Кольцо в знаке проявляется с кадра передачи, мс. */
export const RING_IN_MS = 160;
/** Контур пункта (data-core-away): проявляется с 60 % стягивания, гаснет с 70 % растекания, мс. */
export const AWAY_IN_MS = 200;
export const AWAY_OUT_MS = 160;
/** Кадр передачи из выделения: центр капли — столько px от левой кромки таблетки (значок — с 14 px). */
export const HAND_X = 8;
/** Ось коридора — левое поле меню, px от его левой кромки (spec-3-motion §1). */
export const HALL_X = 8;

/** Таблетка на пункте b стягивается в каплю у левой кромки (HAND_X): как первый отрезок inFrames. */
export function contractFrames(b: PillBox, r: number): Keyframe[] {
  const hand = { x: b.left + HAND_X, y: b.top + b.height / 2, r };
  return [{ ...pillFrame(b), easing: 'cubic-bezier(0.5, 0, 0.3, 1)' }, drop(hand, LAND_K)];
}

/** Капля у левой кромки (HAND_X) растекается в таблетку на пункте b: как хвост outFrames. */
export function spreadFrames(b: PillBox, r: number): Keyframe[] {
  const hand = { x: b.left + HAND_X, y: b.top + b.height / 2, r };
  return [{ ...drop(hand, LAND_K), easing: 'cubic-bezier(0.3, 1.2, 0.5, 1)' }, pillFrame(b)];
}
